// src/pages/Lounge/ExploreUniverse/ExploreUniverse.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import MrNoty from "@/assets/UI/searchClub/searchMr.webp";
import MrsNoty from "@/assets/UI/searchClub/searchMrs.webp";
import CityImage from "@/assets/UI/city.webp";
import api from "@/utils/axios";

import cls from "./ExploreUniverse.module.scss";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [0.5, 0.65, 0.5],
    scale: [1, 1.04, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [1, 0.62, 1],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const ExploreUniverse = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedLang, setSelectedLang] = useState("all");
  const [expandedCards, setExpandedCards] = useState({});

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/v1/lounge/cities/explore");
        if (cancelled) return;
        setCities(Array.isArray(res.data) ? res.data : []);
        setErrorMsg("");
      } catch {
        if (cancelled) return;
        setErrorMsg(
          t("exploreUniverse.errors.loadFailed", "Could not load cities."),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const openCities = useMemo(
    () => cities.filter((c) => c.cityStatus === "open"),
    [cities],
  );

  const closedCities = useMemo(
    () => cities.filter((c) => c.cityStatus !== "open"),
    [cities],
  );

  const languageOptions = useMemo(() => {
    const set = new Set();
    openCities.forEach((c) => {
      if (c.cityLanguage) set.add(c.cityLanguage);
    });
    return Array.from(set);
  }, [openCities]);

  const filteredOpenCities = useMemo(() => {
    if (selectedLang === "all") return openCities;
    return openCities.filter((c) => c.cityLanguage === selectedLang);
  }, [openCities, selectedLang]);

  const toggleExpand = (id) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getLanguageLabel = (code) => {
    if (!code) return "";
    const key = `exploreUniverse.filters.lang.${code}`;
    const value = t(key);
    return value === key ? code.toUpperCase() : value;
  };

  const getStatusLabel = (status) => {
    if (status === "open") return t("exploreUniverse.cities.statusOpen");
    return t("exploreUniverse.cities.statusClosed");
  };

  const getCityImageSrc = (city) => {
    if (!city) return CityImage;

    // Preferred: signed CloudFront URL
    if (city.avatarThumb) {
      if (typeof city.avatarThumb === "string") return city.avatarThumb;
      if (
        typeof city.avatarThumb === "object" &&
        (city.avatarThumb.url || city.avatarThumb.rawUrl)
      ) {
        return city.avatarThumb.url || city.avatarThumb.rawUrl;
      }
    }

    // Fallback: backend might already send a full URL as key
    if (city.cityAvatarTumbKey) return city.cityAvatarTumbKey;

    return CityImage;
  };

  const renderCityCard = (city, isClosedSection = false) => {
    const imageSrc = getCityImageSrc(city);

    const bodyIntro =
      city.cityIntro && city.cityIntro.trim().length > 0
        ? city.cityIntro
        : city.cityDescription || "";

    const languageLabel = getLanguageLabel(city.cityLanguage);
    const statusLabel = getStatusLabel(city.cityStatus);

    const metaText = t("exploreUniverse.cities.languageTag", {
      language: languageLabel,
      status: statusLabel,
    });

    // Closed: no link, disabled button
    if (isClosedSection) {
      return (
        <div key={city.cityId} className={cls.clubCard}>
          <div className={cls.clubImageWrap}>
            <img src={imageSrc} alt={city.cityName} className={cls.clubImage} />
          </div>

          <div className={cls.clubBody}>
            <h4 className={cls.clubName}>{city.cityName}</h4>
            <p className={cls.clubMeta}>{metaText}</p>

            {bodyIntro && (
              <p
                className={
                  expandedCards[city.cityId]
                    ? cls.clubIntro
                    : `${cls.clubIntro} ${cls.clampedText}`
                }
              >
                {bodyIntro}
              </p>
            )}

            {bodyIntro.length > 120 && (
              <button
                type="button"
                className={cls.readMoreBtn}
                onClick={(e) => {
                  e.preventDefault();
                  toggleExpand(city.cityId);
                }}
              >
                {expandedCards[city.cityId]
                  ? t("exploreUniverse.readLess")
                  : t("exploreUniverse.readMore")}
              </button>
            )}
          </div>

          <div className={cls.clubFooter}>
            <button
              type="button"
              className={`${cls.clubVisitBtn} ${cls.clubVisitBtnDisabled}`}
              disabled
            >
              {t("exploreUniverse.cities.closedButton")}
            </button>
          </div>
        </div>
      );
    }

    // Open: Link card
    return (
      <Motion.div
        key={city.cityId}
        whileHover={{ y: -4, scale: 1.02 }}
        transition={{ duration: 0.16 }}
      >
        <Link
          to={`/lounge/cities/single/${city.cityId}`}
          className={cls.clubCard}
        >
          <div className={cls.clubImageWrap}>
            <img src={imageSrc} alt={city.cityName} className={cls.clubImage} />
          </div>

          <div className={cls.clubBody}>
            <h4 className={cls.clubName}>{city.cityName}</h4>
            <p className={cls.clubMeta}>{metaText}</p>

            {bodyIntro && (
              <p
                className={
                  expandedCards[city.cityId]
                    ? cls.clubIntro
                    : `${cls.clubIntro} ${cls.clampedText}`
                }
              >
                {bodyIntro}
              </p>
            )}

            {bodyIntro.length > 120 && (
              <button
                type="button"
                className={cls.readMoreBtn}
                onClick={(e) => {
                  e.preventDefault();
                  toggleExpand(city.cityId);
                }}
              >
                {expandedCards[city.cityId]
                  ? t("exploreUniverse.readLess")
                  : t("exploreUniverse.readMore")}
              </button>
            )}
          </div>

          <div className={cls.clubFooter}>
            <span className={cls.clubVisitBtn}>
              {t("exploreUniverse.cities.visit")}
            </span>
          </div>
        </Link>
      </Motion.div>
    );
  };

  // ✅ Loader gate AFTER hooks (safe)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Header card */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>
                  {t("exploreUniverse.card.title")}
                </h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore} ${cls.clubMiniBtnActive}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 0-.59l7.4-2.47a.5.5 0 01.63.63z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 5L20.49 19l-5-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* Hero */}
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroArtFrame}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                  aria-hidden="true"
                >
                  <div className={cls.heroArtWrap}>
                    <Motion.img
                      src={MrNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMr}`}
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                    />
                    <Motion.img
                      src={MrsNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMrs}`}
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                    />
                  </div>
                </Motion.div>

                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroCard}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                >
                  <span className={cls.searchPromptLabel}>
                    {t("exploreUniverse.hero.label")}
                  </span>
                  <h2 className={cls.searchPromptTitle}>
                    {t("exploreUniverse.hero.title")}
                  </h2>
                  <p className={cls.searchPromptText}>
                    {t("exploreUniverse.hero.text")}
                  </p>
                </Motion.div>
              </div>
            </section>

            {/* Cities */}
            <section className={cls.clubsGridSection}>
              {errorMsg && <p className={cls.errorText}>{errorMsg}</p>}

              <div className={cls.clubsGridHeader}>
                <h3 className={cls.cityListTitle}>
                  {t("exploreUniverse.cities.openTitle")}
                </h3>
                <p className={cls.cityListMeta}>
                  {t("exploreUniverse.cities.openMeta")}
                </p>
              </div>

              {languageOptions.length > 0 && (
                <div className={cls.filtersRow}>
                  <span className={cls.filterLabel}>
                    {t("exploreUniverse.filters.languageLabel")}
                  </span>
                  <select
                    className={cls.filterSelect}
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                  >
                    <option value="all">
                      {t("exploreUniverse.filters.allLanguages")}
                    </option>
                    {languageOptions.map((code) => (
                      <option key={code} value={code}>
                        {getLanguageLabel(code)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {filteredOpenCities.length === 0 ? (
                <p className={cls.cityListMeta}>
                  {t("exploreUniverse.cities.noOpen")}
                </p>
              ) : (
                <div className={cls.clubsGrid}>
                  {filteredOpenCities.map((city) =>
                    renderCityCard(city, false),
                  )}
                </div>
              )}

              {closedCities.length > 0 && (
                <div className={cls.closedSection}>
                  <div className={cls.clubsGridHeader}>
                    <h3 className={cls.cityListTitle}>
                      {t("exploreUniverse.cities.closedTitle")}
                    </h3>
                    <p className={cls.cityListMeta}>
                      {t("exploreUniverse.cities.closedMeta")}
                    </p>
                  </div>

                  <div className={cls.clubsGrid}>
                    {closedCities.map((city) => renderCityCard(city, true))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreUniverse;
